import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { toast } from 'react-toastify';
import Select from 'react-select';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import AllocationModal from '../components/paperallocations/AllocationModal';
import AllocationDetailsModal from '../components/paperallocations/AllocationDetailsModal';
import EmailTriggerModal from '../components/paperallocations/EmailTriggerModal';
import EmailLogsModal from '../components/paperallocations/EmailLogsModal';

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? '#3949ab' : '#e0e0e0',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(26, 35, 126, 0.1)' : 'none',
    '&:hover': { borderColor: '#3949ab' },
    fontSize: 14,
    minHeight: 38,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 14,
    backgroundColor: state.isSelected ? '#1a237e' : state.isFocused ? '#e8eaf6' : 'white',
  }),
  placeholder: (base) => ({ ...base, color: '#9e9e9e' }),
};

function PaperUserAllocations() {
  const { user } = useAuth();
  const tenantId = user?.tenantId;

  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [userCtx, setUserCtx] = useState({ isAdmin: false, isChairman: false });
  const [viewMode, setViewMode] = useState('admin');

  const [bosList, setBosList] = useState([]);
  const [selectedBos, setSelectedBos] = useState(null);

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [courseStatus, setCourseStatus] = useState('All');
  const searchTimer = useRef();
  const pageSize = 50;

  // Selection (checkboxes)
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Modals
  const [allocateCourse, setAllocateCourse] = useState(null);
  const [detailsCourse, setDetailsCourse] = useState(null);
  const [detailsFocus, setDetailsFocus] = useState('all');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailLogsCourse, setEmailLogsCourse] = useState(null);

  const sessionOptions = useMemo(
    () => sessions.map((s) => ({
      value: s.AcademicSessionID,
      label: `${s.YearName} - ${s.SessionName}`,
    })),
    [sessions]
  );

  const bosOptions = useMemo(
    () => bosList.map((b) => ({ value: b.BOSID, label: b.BOSName })),
    [bosList]
  );

  // Which courses are currently checked
  const selectedCourses = useMemo(
    () => courses.filter((c) => selectedIds.has(c.CourseID)),
    [courses, selectedIds]
  );

  // Only allow email on allocated courses
  const allocatedSelected = useMemo(
    () => selectedCourses.filter((c) => c.AllocationStatus === 'Allocated'),
    [selectedCourses]
  );

  // Load sessions + BOS list
  useEffect(() => {
    if (!tenantId) return;
    (async () => {
      try {
        const [sessRes, bosRes] = await Promise.all([
          api.get(`/paper-allocations/sessions/${tenantId}`),
          api.get(`/bos/list/${tenantId}`),
        ]);
        if (sessRes.data.success) {
          setSessions(sessRes.data.data);
          if (sessRes.data.data.length > 0) {
            const first = sessRes.data.data[0];
            setSelectedSession({ value: first.AcademicSessionID, label: `${first.YearName} - ${first.SessionName}` });
          }
        }
        if (bosRes.data.success) {
          setBosList(bosRes.data.data);
        }
      } catch {
        toast.error('Failed to load data');
      }
    })();
  }, [tenantId]);

  // Load user context on session change
  useEffect(() => {
    if (!selectedSession) return;
    (async () => {
      try {
        const res = await api.get(`/paper-allocations/user-context/${selectedSession.value}`);
        if (res.data.success) {
          setUserCtx(res.data.data);
          if (res.data.data.isAdmin) setViewMode('admin');
          else if (res.data.data.isChairman) setViewMode('chairman');
        }
      } catch { /* no-op */ }
    })();
  }, [selectedSession]);

  const loadCourses = useCallback(async () => {
    if (!tenantId || !selectedSession) return;
    try {
      setLoading(true);
      const params = { academicSessionId: selectedSession.value, page, pageSize, search, courseStatus, viewMode };
      if (selectedBos) params.bosId = selectedBos.value;
      const res = await api.get(`/paper-allocations/courses/${tenantId}`, { params });
      if (res.data.success) {
        setCourses(res.data.data);
        setPagination(res.data.pagination);
      }
    } catch {
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  }, [tenantId, selectedSession, page, search, courseStatus, viewMode, selectedBos]);

  useEffect(() => { loadCourses(); }, [loadCourses]);

  // Clear selection on filter/session/page change
  useEffect(() => { setSelectedIds(new Set()); }, [selectedSession, page, search, courseStatus, viewMode, selectedBos]);

  const handleSearchChange = (val) => {
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setSearch(val); setPage(1); }, 400);
  };

  // Checkbox handlers
  const toggleSelectAll = () => {
    if (selectedIds.size === courses.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(courses.map((c) => c.CourseID)));
    }
  };

  const toggleSelect = (courseId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(courseId)) next.delete(courseId); else next.add(courseId);
      return next;
    });
  };

  const openDetails = (course, focus = 'all') => {
    setDetailsCourse(course);
    setDetailsFocus(focus);
  };

  return (
    <div className="page-card">
      <div className="page-card-header" style={{ borderBottom: '1px solid #e0e0e0', paddingBottom: 16 }}>
        <h3 style={{ margin: 0 }}>
          Paper User Allocations
          {pagination.total > 0 && (
            <span style={{ fontWeight: 400, fontSize: 14, color: '#616161', marginLeft: 8 }}>({pagination.total})</span>
          )}
        </h3>
        {selectedIds.size > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 13, color: '#616161' }}>
              {selectedIds.size} selected
              {allocatedSelected.length < selectedIds.size && (
                <span style={{ color: '#e65100' }}> ({allocatedSelected.length} allocated)</span>
              )}
            </span>
            <button
              className="btn btn-email"
              onClick={() => {
                if (allocatedSelected.length === 0) {
                  return toast.error('No allocated courses selected. Only allocated courses can receive emails.');
                }
                setShowEmailModal(true);
              }}
            >
              ✉ Send Emails
            </button>
          </div>
        )}
      </div>

      {/* Top filters */}
      <div className="alloc-toolbar" style={{ flexWrap: 'wrap' }}>
        <div className="form-group" style={{ marginBottom: 0, minWidth: 280 }}>
          <label className="required-label">Academic Session</label>
          <Select
            options={sessionOptions}
            value={selectedSession}
            onChange={(val) => { setSelectedSession(val); setPage(1); }}
            placeholder="Select Academic Session"
            isSearchable
            styles={selectStyles}
          />
        </div>

        {userCtx.isAdmin && userCtx.isChairman && (
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>View As</label>
            <div className="filter-pills">
              <button type="button" className={`filter-pill ${viewMode === 'admin' ? 'active' : ''}`}
                onClick={() => { setViewMode('admin'); setPage(1); }}>All Courses</button>
              <button type="button" className={`filter-pill ${viewMode === 'chairman' ? 'active' : ''}`}
                onClick={() => { setViewMode('chairman'); setPage(1); }}>My BOS Courses</button>
            </div>
          </div>
        )}

        <div className="form-group" style={{ marginBottom: 0, minWidth: 180 }}>
          <label>BOS</label>
          <Select
            options={bosOptions}
            value={selectedBos}
            onChange={(val) => { setSelectedBos(val); setPage(1); }}
            placeholder="All BOS"
            isClearable
            isSearchable
            styles={selectStyles}
          />
        </div>

        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Status</label>
          <div className="filter-pills">
            {['All', 'Pending', 'Allocated'].map((s) => (
              <button key={s} type="button"
                className={`filter-pill ${courseStatus === s ? 'active' : ''}`}
                onClick={() => { setCourseStatus(s); setPage(1); }}>
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 240 }}>
          <label>Search</label>
          <div className="search-box">
            <span className="search-icon">🔍</span>
            <input type="text" placeholder="Search by course code or name..."
              value={searchInput} onChange={(e) => handleSearchChange(e.target.value)} />
          </div>
        </div>
      </div>

      {!selectedSession ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Select an Academic Session to view courses.</div>
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Loading...</div>
      ) : courses.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          {viewMode === 'chairman' ? 'No courses allocated to BOS where you are chairman.' : 'No courses found.'}
        </div>
      ) : (
        <>
          <table className="data-table" style={{ marginTop: 16 }}>
            <thead>
              <tr>
                <th style={{ width: 42, textAlign: 'center' }}>
                  <input type="checkbox" checked={selectedIds.size === courses.length && courses.length > 0}
                    onChange={toggleSelectAll} title="Select All" />
                </th>
                <th>Course Code</th>
                <th>Course Name</th>
                <th style={{ textAlign: 'center' }}>Exam Types</th>
                <th style={{ textAlign: 'center' }}>Sets</th>
                <th style={{ textAlign: 'center' }}>Valuators</th>
                <th style={{ textAlign: 'center' }}>Emails</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {courses.map((c) => (
                <tr key={c.CourseID} className={selectedIds.has(c.CourseID) ? 'row-selected' : ''}>
                  <td style={{ textAlign: 'center' }}>
                    <input type="checkbox" checked={selectedIds.has(c.CourseID)}
                      onChange={() => toggleSelect(c.CourseID)} />
                  </td>
                  <td><strong>{c.CourseCode}</strong></td>
                  <td>{c.CourseName}</td>
                  <td style={{ textAlign: 'center' }}>
                    {c.ExamTypesCount > 0 ? (
                      <button className="count-chip" onClick={() => openDetails(c, 'all')}>{c.ExamTypesCount}</button>
                    ) : <span style={{ color: '#999' }}>0</span>}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {c.TotalSets > 0 ? (
                      <button className="count-chip count-chip-blue" onClick={() => openDetails(c, 'setters')}>{c.TotalSets}</button>
                    ) : <span style={{ color: '#999' }}>0</span>}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {c.TotalValuators > 0 ? (
                      <button className="count-chip count-chip-green" onClick={() => openDetails(c, 'valuators')}>{c.TotalValuators}</button>
                    ) : <span style={{ color: '#999' }}>0</span>}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {(c.EmailsSent > 0 || c.EmailsFailed > 0) ? (
                      <button className="count-chip count-chip-email" onClick={() => setEmailLogsCourse(c)}
                        title={`${c.EmailsSent} sent${c.EmailsFailed > 0 ? `, ${c.EmailsFailed} failed` : ''}`}>
                        {c.EmailsSent > 0 && <span className="email-sent-count">{c.EmailsSent} ✓</span>}
                        {c.EmailsFailed > 0 && <span className="email-failed-count">{c.EmailsFailed} ✗</span>}
                      </button>
                    ) : <span style={{ color: '#999' }}>-</span>}
                  </td>
                  <td>
                    <span className={`badge ${c.AllocationStatus === 'Allocated' ? 'badge-active' : 'badge-pending'}`}>
                      {c.AllocationStatus}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn btn-outline btn-sm"
                        onClick={() => setAllocateCourse(c)}>
                        {c.AllocationStatus === 'Allocated' ? 'Manage' : 'Allocate'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {pagination.totalPages > 1 && (
            <div className="pagination-bar">
              <button className="btn btn-outline btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
              <span className="pagination-info">Page {page} of {pagination.totalPages} &middot; {pagination.total} courses</span>
              <button className="btn btn-outline btn-sm" disabled={page >= pagination.totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {allocateCourse && selectedSession && (
        <AllocationModal tenantId={tenantId} course={allocateCourse} academicSessionId={selectedSession.value}
          onClose={() => setAllocateCourse(null)}
          onSaved={() => { setAllocateCourse(null); loadCourses(); }} />
      )}

      {detailsCourse && selectedSession && (
        <AllocationDetailsModal course={detailsCourse} academicSessionId={selectedSession.value}
          focus={detailsFocus} onClose={() => setDetailsCourse(null)} />
      )}

      {showEmailModal && selectedSession && (
        <EmailTriggerModal tenantId={tenantId} academicSessionId={selectedSession.value}
          selectedCourses={allocatedSelected}
          onClose={() => setShowEmailModal(false)}
          onSent={() => { setShowEmailModal(false); setSelectedIds(new Set()); loadCourses(); }} />
      )}

      {emailLogsCourse && selectedSession && (
        <EmailLogsModal course={emailLogsCourse} academicSessionId={selectedSession.value}
          onClose={() => setEmailLogsCourse(null)} />
      )}
    </div>
  );
}

export default PaperUserAllocations;
